import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import "../../assets/css/custom-ag-grid.css";
import Tree from "rc-tree";
import Split from "react-split";
import SyncIcon from "@mui/icons-material/Sync";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import LaptopMacOutlinedIcon from "@mui/icons-material/LaptopMacOutlined";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useDispatch } from "react-redux";
import { api } from "../../redux/api";
import CommonGrid from "../../components/Repository/CommonGrid";
import ConfirmModal from "../../components/ItemProperties/Forms/FieldDataValidation/ConfirmModal";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ExportModal from "./ExportModal";
import RemoteRevisionHistoryModal from "./RemoteRevisionHistoryModal";

const PasswordCellEditor = forwardRef((props, ref) => {
  const [value, setValue] = useState(props.value ?? props.node?.data?.password ?? "");
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getValue: () => value,
    focus: () => inputRef.current?.focus(),
  }));

  const handleChange = (e) => {
    const v = e.target.value;
    setValue(v);
    props.onValueChange?.(v);
  };

  return (
    <TextField
      inputRef={inputRef}
      size="small"
      fullWidth
      type="password"
      value={value}
      autoFocus
      onChange={handleChange}
      variant="standard"
      sx={{ width: "100%" }}
      inputProps={{ autoComplete: "new-password" }}
    />
  );
});


function DeployToRemoteServer({ urlParams }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [showNewerTemplates, setShowNewerTemplates] = useState(false);
  const currentServer =
    urlParams?.baseUrl || t("LBL_CURRENT_SERVER") || "Current Server";
  const [serverLocation, setServerLocation] = useState("");

  const [leftTreeData, setLeftTreeData] = useState([]);
  const [leftExpandedKeys, setLeftExpandedKeys] = useState([]);
  const [leftSelectedKeys, setLeftSelectedKeys] = useState([]);
  const [loadingLeftTree, setLoadingLeftTree] = useState(false);

  const [rightExpandedKeys, setRightExpandedKeys] = useState([]);
  const [rightSelectedKeys, setRightSelectedKeys] = useState([]);
  const [remoteConnections, setRemoteConnections] = useState([]);
  const [loadingRemoteConnections, setLoadingRemoteConnections] =
    useState(false);
  const [remoteConnectionsHelperText, setRemoteConnectionsHelperText] =
    useState("");
  const [connectionDrawerOpen, setConnectionDrawerOpen] = useState(false);
  const [savingConnection, setSavingConnection] = useState(false);
  const [hasConnectionChanges, setHasConnectionChanges] = useState(false);
  const [newConnectionError, setNewConnectionError] = useState("");
  const [deleteConnectionModalOpen, setDeleteConnectionModalOpen] =
    useState(false);
  const [deleteConnectionMessage, setDeleteConnectionMessage] = useState("");
  const [pendingDeleteConnectionRow, setPendingDeleteConnectionRow] =
    useState(null);
  const [connectionGridRows, setConnectionGridRows] = useState([
    {
      id: 0,
      name: "",
      url: "",
      alias: "",
      username: "",
      authType: "BGSUSER",
      password: "",
      connectionId: "",
      key: "",
    },
  ]);
  const [connectionGridApi, setConnectionGridApi] = useState();
  const [openExportCommentModal, setOpenExportCommentModal] = useState(false);

  const [remoteToken, setRemoteToken] = useState(null);
  const [remoteLoginLoading, setRemoteLoginLoading] = useState(false);
  const [remoteLoginError, setRemoteLoginError] = useState("");
  const [rightTreeData, setRightTreeData] = useState([]);
  const [loadingRightTree, setLoadingRightTree] = useState(false);
  const [revisionHistoryModalOpen, setRevisionHistoryModalOpen] =
    useState(false);
  const [revisionHistoryNode, setRevisionHistoryNode] = useState(null);
  const [pendingRightSelectionKey, setPendingRightSelectionKey] = useState(null);
  const leftLastSelectedKeyRef = useRef(null);
  const rightLastSelectedKeyRef = useRef(null);
  const nodeIcon = {
    inboundTemplate: "GET_DATA",
    outboundTemplate: "SEND_DATA",
    workflow: "CALL_WORKFLOW",
    workflow_group: "RECORD",
    DOCUMENT: "TRACKINGREPORT",
    template_folder: "RECORD",
  };

  const transformFileListToTreeNodes = (
    fileList,
    parentKey,
    parentWfGroup,
    parentFolderPath,
  ) => {
    return fileList.map((file, idx) => ({
      title: file?.name,
      key: `${parentKey}-file-${idx}`,
      type: file?.templateType,
      workflowGroup: parentWfGroup,
      folderPath: parentFolderPath,
      server: file?.server,
      status: file?.status,
      isDocument: file?.isDocument,
      isTemplateFile: file?.isTemplateFile,
      isLocked: file?.isLocked,
      lockStatus: file?.lockStatus,
      children: [],
    }));
  };

  const transformFolderListToTree = useCallback((folderList) => {
    return folderList.map((folder, folderIdx) => ({
      title: folder.name,
      key: `dp-${folderIdx}`,
      type: folder.type,
      children: (folder.templateFolders || [])
        .filter((child) => child?.fileCount > 0)
        .map((child, childIdx) => ({
          title: child?.name,
          key: `dp-${folderIdx}-${childIdx}`,
          type: child?.type,
          fileCount: child?.files?.length || child?.fileCount,
          workflowGroup: folder?.name,
          folderPath: child?.folderPath,
          workflow_only: child.templateType === "WORKFLOW" ? "Yes" : "No",
          children: transformFileListToTreeNodes(
            child?.files || [],
            `dp-${folderIdx}-${childIdx}`,
            folder?.name,
            child?.folderPath,
          ),
        })),
    }));
  }, []);

  const transformRemoteFolderListToTree = useCallback((folderList) => {
    return (folderList || []).map((folder, folderIdx) => ({
      title: folder.name,
      key: `rp-${folderIdx}`,
      type: folder.type,
      workflowGroup: folder.name,
      children: (folder.templateFolders || [])
        .filter((child) => child?.fileCount > 0 || child?.files?.length > 0)
        .map((child, childIdx) => ({
          title: child?.name,
          key: `rp-${folderIdx}-${childIdx}`,
          type: child?.type,
          fileCount: child?.files?.length || child?.fileCount,
          workflowGroup: folder?.name,
          folderPath: child?.folderPath,
          children: transformFileListToTreeNodes(
            child?.files || [],
            `rp-${folderIdx}-${childIdx}`,
            folder?.name,
            child?.folderPath,
          ),
        })),
    }));
  }, []);

  const refreshLeftTreeData = useCallback(() => {
    if (!urlParams?.account) {
      return;
    }
    setLoadingLeftTree(true);
    dispatch(
      api.endpoints.getFolderList.initiate(
        {
          account: urlParams.account,
          userspace: false,
          server: true,
        },
        { forceRefetch: true },
      ),
    )
      .then((result) => {
        setLoadingLeftTree(false);
        if (result.data?.current_state === "SUCCESS") {
          const tree = transformFolderListToTree(
            result?.data?.data?.workflowGroups || [],
          );
          setLeftTreeData(tree);
        } else {
          setLeftTreeData([]);
        }
      })
      .catch(() => {
        setLoadingLeftTree(false);
        setLeftTreeData([]);
      });
  }, [dispatch, transformFolderListToTree, urlParams?.account]);

  useEffect(() => {
    refreshLeftTreeData();
  }, [refreshLeftTreeData]);

  const refreshRemoteConnections = useCallback(() => {
    if (!urlParams?.account) {
      setRemoteConnections([]);
      setServerLocation("");
      setRemoteConnectionsHelperText(
        t("MSG_REMOTE_CONN_ACCOUNT_MISSING") ||
          "Account is missing. Unable to load remote connections.",
      );
      return;
    }

    setLoadingRemoteConnections(true);
    setRemoteConnectionsHelperText("");

    dispatch(
      api.endpoints.listRemoteConnections.initiate(
        { account: urlParams.account },
        { forceRefetch: true },
      ),
    )
      .then((result) => {
        setLoadingRemoteConnections(false);

        if (result?.error) {
          const errorMessage =
            result?.error?.data?.message ||
            result?.error?.error ||
            t("MSG_REMOTE_CONN_LOAD_ERROR") ||
            "Unable to load remote connections.";
          setRemoteConnections([]);
          setServerLocation("");
          setRemoteConnectionsHelperText(errorMessage);
          return;
        }

        if (result?.data?.current_state !== "SUCCESS") {
          setRemoteConnections([]);
          setServerLocation("");
          setRemoteConnectionsHelperText(
            result?.data?.message ||
              t("MSG_REMOTE_CONN_LOAD_FAILED") ||
              "Failed to load remote connections.",
          );
          return;
        }

        const connections = Array.isArray(result?.data?.data?.connections)
          ? result.data.data.connections
          : [];
        setRemoteConnections(connections);

        if (connections.length === 0) {
          setServerLocation("");
          setRemoteConnectionsHelperText(
            t("MSG_REMOTE_CONN_NONE_FOUND") || "No remote connections found.",
          );
          return;
        }

        setServerLocation((previousValue) => {
          const hasSelectedConnection = connections.some(
            (connection) => connection.id === previousValue,
          );
          return hasSelectedConnection ? previousValue : connections[0].id;
        });
      })
      .catch(() => {
        setLoadingRemoteConnections(false);
        setRemoteConnections([]);
        setServerLocation("");
        setRemoteConnectionsHelperText(
          t("MSG_REMOTE_CONN_LOAD_ERROR") ||
            "Unable to load remote connections.",
        );
      });
  }, [dispatch, urlParams?.account]);

  useEffect(() => {
    refreshRemoteConnections();
  }, [refreshRemoteConnections]);

  useEffect(() => {
    setRemoteToken(null);
    setRemoteLoginError("");
    setRightTreeData([]);
  }, [serverLocation]);

  const refreshRightTreeData = useCallback(() => {
    if (!remoteToken || !serverLocation || !urlParams?.account) {
      setRightTreeData([]);
      return;
    }
    const conn = remoteConnections.find((c) => c.id === serverLocation);
    const remoteUrl = conn?.url || "";
    setLoadingRightTree(true);
    dispatch(
      api.endpoints.listRemoteTemplates.initiate(
        {
          account: urlParams.account,
          remote_token: remoteToken,
          remote_url: remoteUrl,
          user: urlParams.user,
          include_files: true,
          recursive: false,
        },
        { forceRefetch: true },
      ),
      { forceRefetch: true },
    )
      .then((result) => {
        setLoadingRightTree(false);
        if (result?.data?.current_state === "SUCCESS") {
          const wfGroups = result?.data?.data?.workflowGroups || [];
          const tree = transformRemoteFolderListToTree(wfGroups);
          setRightTreeData(tree);

          // After refresh, if we have a pending template key to focus on,
          // expand its folder path and select that item on the right tree.
          if (pendingRightSelectionKey) {
            const findPathByTemplateKey = (nodes, ancestors) => {
              for (const node of nodes || []) {
                const key = getTemplateKey(node);
                const nextAncestors = [...ancestors, node.key];
                if (key === pendingRightSelectionKey && node?.isTemplateFile) {
                  return nextAncestors;
                }
                const childPath = findPathByTemplateKey(
                  node.children || [],
                  nextAncestors,
                );
                if (childPath) return childPath;
              }
              return null;
            };

            const pathKeys = findPathByTemplateKey(tree, []);
            if (pathKeys && pathKeys.length > 0) {
              const fileKey = pathKeys[pathKeys.length - 1];
              const parentKeys = pathKeys.slice(0, -1);
              setRightSelectedKeys([fileKey]);
              setRightExpandedKeys((prev) =>
                Array.from(new Set([...(prev || []), ...parentKeys])),
              );
            }
            setPendingRightSelectionKey(null);
          }
        } else {
          setRightTreeData([]);
        }
      })
      .catch(() => {
        setLoadingRightTree(false);
        setRightTreeData([]);
      });
  }, [
    dispatch,
    remoteToken,
    serverLocation,
    urlParams?.account,
    urlParams?.user,
    remoteConnections,
    transformRemoteFolderListToTree,
  ]);

  useEffect(() => {
    if (remoteToken && serverLocation) {
      refreshRightTreeData();
    } else {
      setRightTreeData([]);
    }
  }, [remoteToken, serverLocation, refreshRightTreeData]);

  const resetConnectionForm = useCallback(() => {
    setNewConnectionError("");
    setConnectionGridRows([
      {
        id: 0,
        name: "",
        url: "",
        alias: "",
        username: "",
        authType: "BGSUSER",
        password: "",
        connectionId: "",
        key: "",
      },
    ]);
    setHasConnectionChanges(false);
  }, []);

  const buildGridRowsFromConnections = useCallback((connections = []) => {
    const existingRows = (connections || []).map((connection, index) => ({
      id: index,
      connectionId: connection.id,
      name: connection.name || "",
      url: connection.url || "",
      alias: connection.alias || "",
      username: connection.username || "",
      password: "••••••",
      authType: connection.authType || "BGSUSER",
      apiToken: "", // token is never returned from API; keep field for new/edited rows
      key: connection.url || connection.name || connection.alias || "",
    }));
    return [
      ...existingRows,
      {
        id: existingRows.length,
        connectionId: "",
        name: "",
        url: "",
        alias: "",
        username: "",
        password: "",
        authType: "BGSUSER",
        key: "",
      },
    ];
  }, []);

  const handleOpenConnectionDrawer = () => {
    resetConnectionForm();
    setConnectionGridRows(buildGridRowsFromConnections(remoteConnections));
    setConnectionDrawerOpen(true);
  };

  const handleCloseConnectionDrawer = () => {
    if (savingConnection) {
      return;
    }
    setConnectionDrawerOpen(false);
    setNewConnectionError("");
  };

  const buildModifyPayloadFromRows = (rows) => {
    const modifyPayload = {
      create: [],
      update: {},
      delete: [],
    };

    const existingById = new Map(
      (remoteConnections || []).map((conn) => [conn.id, conn]),
    );

    const rowsById = new Map();

    rows.forEach((row) => {
      const url = (row?.url || "").trim();
      const username = (row?.username || "").trim();
      const name = (row?.name || row?.alias || url).trim();

      // Skip completely empty placeholder rows
      if (!row?.connectionId && !url && !username && !name) {
        return;
      }

      if (row?.connectionId) {
        rowsById.set(row.connectionId, row);
      }

      const basePayload = {
        name,
        alias: (row?.alias || "").trim(),
        url,
        username,
        authType: row?.authType || "BGSUSER",
      };

      const passwordVal = (row?.password || "").trim();
      if (passwordVal && passwordVal !== "••••••") {
        if ((row?.authType || "BGSUSER") === "SAML") {
          basePayload.apiToken = passwordVal;
        } else {
          basePayload.password = passwordVal;
        }
      }

      if (row?.connectionId) {
        // Existing connection: treat as update
        modifyPayload.update[row.connectionId] = basePayload;
      } else {
        // New connection: treat as create
        modifyPayload.create.push(basePayload);
      }
    });

    // Deletions: any existing connection not present in rows
    existingById.forEach((_, id) => {
      if (!rowsById.has(id)) {
        modifyPayload.delete.push(id);
      }
    });

    return modifyPayload;
  };

  const getConnectionRowsFromGrid = useCallback(() => {
    const rows = [];
    if (!connectionGridApi) {
      return rows;
    }
    connectionGridApi.forEachNode((node) => {
      if (node?.data) {
        rows.push(node.data);
      }
    });
    return rows;
  }, [connectionGridApi]);

  const deleteConnectionRowFromGrid = useCallback(
    (rowId) => {
      const filteredRows = getConnectionRowsFromGrid().filter(
        (row) => row?.id !== rowId,
      );
      const normalizedRows = filteredRows.map((row, index) => ({
        ...row,
        id: index,
        key: row?.key || row?.url || "",
      }));
      const hasEmptyRow = normalizedRows.some(
        (row) =>
          !row?.connectionId &&
          !(row?.url || "").trim() &&
          !(row?.username || "").trim(),
      );
      if (!hasEmptyRow) {
        normalizedRows.push({
          id: normalizedRows.length,
          connectionId: "",
          url: "",
          username: "",
          password: "",
          key: "",
        });
      }
      setConnectionGridRows(normalizedRows);
      if (connectionGridApi) {
        connectionGridApi.setGridOption("rowData", normalizedRows);
      }
      setHasConnectionChanges(true);
    },
    [connectionGridApi, getConnectionRowsFromGrid],
  );

  const handleDeleteConnection = useCallback(
    (row) => {
      if (!row) {
        return;
      }
      const connectionLabel =
        (row?.url || row?.name || row?.alias || "").trim() || "this connection";
      setPendingDeleteConnectionRow(row);
      setDeleteConnectionMessage(
        `Are you sure you want to delete "${connectionLabel}"?`,
      );
      setDeleteConnectionModalOpen(true);
    },
    [],
  );

  const handleConfirmDeleteConnection = useCallback(() => {
    const row = pendingDeleteConnectionRow;
    if (!row) {
      setDeleteConnectionModalOpen(false);
      return;
    }

    setNewConnectionError("");

    // Only update local grid; actual delete will be applied in bulk when user clicks Apply
    deleteConnectionRowFromGrid(row.id);
    setDeleteConnectionModalOpen(false);
    setPendingDeleteConnectionRow(null);
  }, [
    deleteConnectionRowFromGrid,
    pendingDeleteConnectionRow,
  ]);

  const handleSaveConnection = () => {
    connectionGridApi?.stopEditing?.();
    const rows = getConnectionRowsFromGrid();

    if (!urlParams?.account) {
      setNewConnectionError(
        t("MSG_REMOTE_CONN_ACCOUNT_MISSING_SIMPLE") || "Account is missing.",
      );
      return;
    }

    const modifyPayload = buildModifyPayloadFromRows(rows);

    const hasChanges =
      modifyPayload.create.length > 0 ||
      Object.keys(modifyPayload.update).length > 0 ||
      modifyPayload.delete.length > 0;
    const selectedConnectionWasUpdated = Boolean(
      serverLocation && Object.prototype.hasOwnProperty.call(modifyPayload.update, serverLocation),
    );
    const wasConnectedToSelected = Boolean(remoteToken && serverLocation);

    if (!hasChanges) {
      setNewConnectionError(
        t("MSG_REMOTE_CONN_NO_CHANGES") || "No changes to apply.",
      );
      setHasConnectionChanges(false);
      return;
    }

    setSavingConnection(true);
    setNewConnectionError("");

    dispatch(
      api.endpoints.modifyRemoteConnections.initiate({
        account: urlParams.account,
        data: modifyPayload,
      }),
    )
      .then((result) => {
        setSavingConnection(false);
        if (result?.error) {
          const errorMessage =
            result?.error?.data?.message ||
            result?.error?.error ||
            t("MSG_REMOTE_CONN_SAVE_ERROR") ||
              "Unable to save remote connections.";
          setNewConnectionError(errorMessage);
          return;
        }

        if (result?.data?.current_state !== "SUCCESS") {
          setNewConnectionError(
            result?.data?.message ||
              t("MSG_REMOTE_CONN_SAVE_ERROR") ||
              "Unable to save remote connections.",
          );
          return;
        }

        setConnectionDrawerOpen(false);
        resetConnectionForm();
        refreshRemoteConnections();
        setHasConnectionChanges(false);
        // If user updated the currently connected connection, reconnect to refresh token/session.
        if (wasConnectedToSelected && selectedConnectionWasUpdated) {
          setRemoteToken(null);
          handleConnectWithSavedConnection();
        }
      })
      .catch(() => {
        setSavingConnection(false);
        setNewConnectionError(
          t("MSG_REMOTE_CONN_SAVE_ERROR") ||
            "Unable to save remote connections.",
        );
      });
  };

  const handleUseConnection = (row) => {
    if (!row) {
      return;
    }
    if (row.connectionId) {
      // Select this saved connection
      setServerLocation(row.connectionId);
      setConnectionDrawerOpen(false);
      // Immediately connect using the saved connection so the UI shows as connected
      handleConnectWithSavedConnection();
      return;
    }
    // For unsaved rows, require user to apply changes first
    setNewConnectionError(
      t("MSG_REMOTE_CONN_APPLY_BEFORE_USE") ||
        "Apply changes to save this connection before using it.",
    );
  };

  const handleConnectWithSavedConnection = useCallback(() => {
    if (!serverLocation || !urlParams?.account || !urlParams?.user) {
      setRemoteLoginError(
        t("MSG_REMOTE_CONN_SELECT_AND_ACCOUNT") ||
          "Select a remote connection and ensure account is set.",
      );
      return;
    }
    setRemoteLoginLoading(true);
    setRemoteLoginError("");

    dispatch(
      api.endpoints.connectRemoteServer.initiate({
        account: urlParams.account,
        data: {
          connection_id: serverLocation,
          account: urlParams.account,
        },
      }),
    )
      .then((result) => {
        setRemoteLoginLoading(false);
        if (result?.error) {
          const msg =
            result?.error?.data?.message ||
            result?.error?.error ||
            t("MSG_REMOTE_CONN_CONNECT_ERROR") ||
              "Unable to connect to remote server.";
          setRemoteLoginError(msg);
          return;
        }
        if (result?.data?.current_state !== "SUCCESS") {
          setRemoteLoginError(
            result?.data?.message ||
              t("MSG_REMOTE_CONN_LOGIN_FAILED") ||
              "Remote login failed.",
          );
          return;
        }
        setRemoteToken(result?.data?.data?.token ?? null);
        setRemoteLoginError("");
      })
      .catch(() => {
        setRemoteLoginLoading(false);
        setRemoteLoginError(
          t("MSG_REMOTE_CONN_CONNECT_ERROR") ||
            "Unable to connect to remote server.",
        );
      });
  }, [dispatch, serverLocation, urlParams?.account, urlParams?.user]);

  const handleDisconnect = useCallback(() => {
    setRemoteToken(null);
    setRemoteLoginError("");
  }, []);

  const connectionGridOptions = {
    columnDefs: [
      {
        headerName: t("LBL_REMOTE_CONN_NAME") || "Name",
        field: "name",
        editable: true,
        suppressKeyboardEvent: () => true,
      },
      {
        headerName: t("LBL_REMOTE_CONN_URL") || "URL",
        field: "url",
        editable: true,
        addNewControl: true,
        addControlLabel: t("LBL_REMOTE_CONN_ADD") || "Add connection",
        suppressKeyboardEvent: () => true,
      },
      {
        headerName: t("LBL_REMOTE_CONN_ALIAS") || "Alias",
        field: "alias",
        editable: true,
        suppressKeyboardEvent: () => true,
      },
      {
        headerName: t("LBL_REMOTE_CONN_USERNAME") || "Username",
        field: "username",
        editable: true,
        suppressKeyboardEvent: () => true,
      },
      {
        headerName: t("LBL_REMOTE_CONN_AUTH_TYPE") || "Auth type",
        field: "authType",
        editable: true,
        suppressKeyboardEvent: () => true,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: ["BGSUSER", "LDAP", "SAML"],
        },
      },
      {
        headerName: t("LBL_REMOTE_CONN_SECRET") || "Password / ApiToken",
        field: "password",
        editable: (params) => !params?.data?.connectionId,
        suppressKeyboardEvent: () => true,
        cellRenderer: (params) => {
          if (params?.data?.connectionId) {
            return (
              <TextField
                size="small"
                fullWidth
                disabled
                value="••••••"
                type="password"
                variant="standard"
                sx={{ "& input": { opacity: 0.8 } }}
              />
            );
          }
          const hasValue =
            params?.value || params?.data?.password;
          if (!params?.data?.connectionId && hasValue) {
            return <span style={{ opacity: 0.9 }}>••••••</span>;
          }
          return null;
        },
        cellEditor: PasswordCellEditor,
        valueFormatter: (params) =>
          params?.value || params?.data?.password ? "••••••" : "",
        valueSetter: (params) => {
          const prev = params.data.password;
          if (params.newValue != null) {
            params.data.password = params.newValue;
          }
          return prev !== params.data.password;
        },
      },
      {
        headerName: "",
        field: "deleteConnection",
        editable: false,
        suppressSizeToFit: true,
        cellRenderer: (params) => {
          const hasValue = Boolean(
            (params?.data?.url || "").trim() || (params?.data?.username || "").trim(),
          );
          if (!hasValue) return "";
          return (
            <IconButton
              size="small"
              title="Delete connection"
              onClick={(event) => {
                event.stopPropagation();
                handleDeleteConnection(params?.data);
              }}>
                <span
                  style={{ cursor: "pointer" }}
                  className="DELETE_MUI"
                  ></span>
            </IconButton>
          );
        },
      },
      {
        headerName: "",
        field: "connectionAction",
        editable: false,
        suppressSizeToFit: true,
        cellRenderer: (params) => {
          const hasValue = Boolean((params?.data?.url || "").trim());
          if (!hasValue) return "";
          return (
            <IconButton
              size="small"
              title="Use connection"
              onClick={(event) => {
                event.stopPropagation();
                handleUseConnection(params?.data);
              }}>
              <LinkOutlinedIcon fontSize="small" />
            </IconButton>
          );
        },
      },
    ],
  };

  const countTreeItems = (nodes) =>
    nodes.reduce(
      (count, node) => count + 1 + countTreeItems(node.children || []),
      0,
    );

  const getAllTreeKeys = (nodes) => {
    const keys = [];
    const walk = (treeNodes) => {
      treeNodes.forEach((node) => {
        keys.push(node.key);
        if (node.children?.length) {
          walk(node.children);
        }
      });
    };
    walk(nodes);
    return keys;
  };

  const switcherIcon = (obj) => {
    if (obj.isLeaf) return null;
    return obj.expanded ? (
      <ArrowDropDownIcon />
    ) : (
      <ArrowRightIcon className="deploy-arrow-icon" />
    );
  };

  const renderNodeIcon = (node) => {
    if (
      (node.type === "INBOUND" ||
        node.type === "OUTBOUND" ||
        node.type === "WORKFLOW" ||
        node.type === "SCHEDULE") &&
      node.isTemplateFile
    ) {
      return <span className={node?.isLocked ? "LOCK" : "SERVER"}></span>;
    }
    return (
      <>
        <span
          className={
            node?.isDocument ? "DOCUMENT" : nodeIcon[node.type]
          }></span>
        {node?.isLocked && <span className="LOCK"></span>}
      </>
    );
  };

  const leftTitleRender = (node) => {
    return (
      <div className="deploy-tree-node-container">
        <div className="deploy-tree-file-column">
          <div className="deploy-tree-file-content">
            <span className="deploy-tree-file-name">{node.title}</span>
          </div>
        </div>
        <div className="deploy-tree-data-columns">
          <div
            className="deploy-tree-server-rev-id"
            style={{ textAlign: "center" }}>
            {node.server?.revisionId || ""}
          </div>
          <div
            className="deploy-tree-server-user"
            style={{ textAlign: "center" }}>
            {node.server?.lastModifiedUser || ""}
          </div>
          <div
            className="deploy-tree-server-modified"
            style={{ textAlign: "center" }}>
            {node.server?.lastModified || ""}
          </div>
        </div>
      </div>
    );
  };

  const rightTitleRender = (node) => {
    return (
      <div className="deploy-tree-node-container">
        <div className="deploy-tree-file-column">
          <div className="deploy-tree-file-content">
            <span className="deploy-tree-file-name">{node.title}</span>
          </div>
        </div>
        <div className="deploy-tree-data-columns">
          <div
            className="deploy-tree-server-rev-id"
            style={{ textAlign: "center" }}>
            {node.server?.revisionId || ""}
          </div>
          <div
            className="deploy-tree-server-user"
            style={{ textAlign: "center" }}>
            {node.server?.lastModifiedUser || ""}
          </div>
          <div
            className="deploy-tree-server-modified"
            style={{ textAlign: "center" }}>
            {node.server?.lastModified || ""}
          </div>
        </div>
      </div>
    );
  };
  
  const findNodeByKey = useCallback((tree, key) => {
    for (const node of tree || []) {
      if (node.key === key) return node;
      const found = findNodeByKey(node.children, key);
      if (found) return found;
    }
    return null;
  }, []);

  /** Template key for matching local vs remote (workflowGroup|folderPath|title) */
  const getTemplateKey = useCallback((node) => {
    if (!node?.workflowGroup || !node?.title) return null;
    const folder = (node.folderPath ?? "").trim();
    return `${node.workflowGroup}|${folder}|${node.title}`;
  }, []);

  /** Collect all file nodes with template key and revision id from a tree */
  const collectFileNodes = useCallback((tree, getRevId) => {
    const out = [];
    const walk = (nodes) => {
      for (const node of nodes || []) {
        const key = getTemplateKey(node);
        if (key != null && node?.key?.includes?.("file")) {
          const revId = getRevId(node);
          out.push({ key, revId, node });
        }
        walk(node.children);
      }
    };
    walk(tree);
    return out;
  }, [getTemplateKey]);

  /**
   * When "Show Newer Templates" is on: show unmatched templates (only on one side) and matched ones where local Rev ID > remote.
   * Only filter out matched templates where local <= remote.
   */
  const { leftTreeShowKeys, rightTreeShowKeys } = useMemo(() => {
    if (!showNewerTemplates) {
      return { leftTreeShowKeys: null, rightTreeShowKeys: null };
    }
    const localFiles = collectFileNodes(leftTreeData, (n) =>
      Number(n?.server?.revisionId),
    );
    const remoteFiles = collectFileNodes(rightTreeData, (n) =>
      Number(n?.server?.revisionId),
    );
    const localByKey = new Map(localFiles.map((f) => [f.key, f.revId]));
    const remoteByKey = new Map(remoteFiles.map((f) => [f.key, f.revId]));

    const leftKeys = new Set();
    for (const { key, revId: localRev } of localFiles) {
      const remoteRev = remoteByKey.get(key);
      const unmatched = remoteRev === undefined;
      const newer = remoteRev !== undefined && !Number.isNaN(localRev) && localRev > remoteRev;
      if (unmatched || newer) leftKeys.add(key);
    }

    const rightKeys = new Set();
    for (const { key, revId: remoteRev } of remoteFiles) {
      const localRev = localByKey.get(key);
      const unmatched = localRev === undefined;
      const newer = localRev !== undefined && !Number.isNaN(remoteRev) && localRev > remoteRev;
      if (unmatched || newer) rightKeys.add(key);
    }

    return { leftTreeShowKeys: leftKeys, rightTreeShowKeys: rightKeys };
  }, [
    showNewerTemplates,
    leftTreeData,
    rightTreeData,
    collectFileNodes,
  ]);

  /** Filter tree to only file nodes whose template key is in keySet; keep parent structure if they have matching children */
  const filterTreeByKeys = useCallback((tree, keySet) => {
    if (!keySet) return tree ?? [];
    const filterNodes = (nodes) => {
      return (nodes || [])
        .map((node) => {
          const templateKey = getTemplateKey(node);
          const isFile = node?.key?.includes?.("file");
          if (isFile) {
            return keySet.has(templateKey) ? node : null;
          }
          const filteredChildren = filterNodes(node.children);
          if (filteredChildren.length === 0) return null;
          return { ...node, children: filteredChildren };
        })
        .filter(Boolean);
    };
    return filterNodes(tree);
  }, [getTemplateKey]);

  const leftTreeDisplay = useMemo(() => {
    if (!showNewerTemplates || leftTreeShowKeys === null) {
      return leftTreeData ?? [];
    }
    return filterTreeByKeys(leftTreeData, leftTreeShowKeys);
  }, [
    showNewerTemplates,
    leftTreeShowKeys,
    leftTreeData,
    filterTreeByKeys,
  ]);

  const rightTreeDisplay = useMemo(() => {
    if (!showNewerTemplates || rightTreeShowKeys === null) {
      return rightTreeData ?? [];
    }
    return filterTreeByKeys(rightTreeData, rightTreeShowKeys);
  }, [
    showNewerTemplates,
    rightTreeShowKeys,
    rightTreeData,
    filterTreeByKeys,
  ]);

  const buildSelectedItemsFromLeftTree = useCallback(() => {
    const items = [];
    const account = urlParams?.account || "";
    for (const key of leftSelectedKeys) {
      const node = findNodeByKey(leftTreeData, key);
      if (!node) continue;
      if (node.type === "workflow_group") {
        items.push({
          name: node.title,
          path: `${account}/${node.title}`,
          type: "workflow_group",
        });
      } else if (node.type === "template_folder") {
        items.push({
          name: node.title,
          path: `${account}/${node.workflowGroup}/${node.folderPath || node.title}`,
          type: "template_folder",
        });
      } else if (node.isTemplateFile && node.title) {
        items.push({
          name: node.title,
          path: `${account}/${node.workflowGroup}/${node.folderPath || ""}/${node.title}`.replace(
            /\/+/g,
            "/",
          ),
          type: "file",
        });
      }
    }
    return items;
  }, [
    leftSelectedKeys,
    leftTreeData,
    findNodeByKey,
    urlParams?.account,
  ]);

  const handleExport = () => {
    // Remember which template we just deployed so we can re-focus it on the remote side
    if (leftSelectedKeys.length > 0) {
      let targetNode = null;
      for (const key of leftSelectedKeys) {
        const node = findNodeByKey(leftTreeData, key);
        if (node?.isTemplateFile) {
          targetNode = node;
          break;
        }
      }
      if (!targetNode) {
        const fallbackNode = findNodeByKey(leftTreeData, leftSelectedKeys[0]);
        if (fallbackNode?.isTemplateFile) {
          targetNode = fallbackNode;
        }
      }
      const templateKey = targetNode ? getTemplateKey(targetNode) : null;
      if (templateKey) {
        setPendingRightSelectionKey(templateKey);
      }
    }
    setOpenExportCommentModal(true);
  };
  
  const renderPanel = ({
    title,
    titleIcon,
    locationLabel,
    locationValue,
    onLocationChange,
    treeData,
    expandedKeys,
    selectedKeys,
    onExpand,
    onSelect,
    titleRender,
    loading,
    itemCount,
    headerType,
    onExpandAll,
    onCollapseAll,
    locationType = "select",
    locationOptions = [],
    locationLoading = false,
    locationError = "",
    locationDisabled = false,
    locationEndAdornment = null,
    locationConnectAction = null,
    onRightClick = null,
  }) => (
    <section className="deploy-remote-panel">
      <div className="deploy-remote-panel-header">
        <div className="deploy-remote-panel-title-wrap">
          <span className="deploy-remote-panel-title-icon">{titleIcon}</span>
          <Typography variant="h6" className="deploy-remote-panel-title">
            {title}
          </Typography>
        </div>
      </div>

      <div className="deploy-remote-panel-controls">
        {locationType === "text" ? (
          <TextField
            size="small"
            fullWidth
            label={locationLabel}
            value={locationValue || ""}
            InputProps={{ readOnly: true }}
          />
        ) : (
          <FormControl className="width-100" size="small">
            <Stack direction="row" spacing={0.5} alignItems="flex-start">
              <TextField
                size="small"
                fullWidth
                select
                label={locationLabel}
                value={locationValue || ""}
                onChange={(event) => onLocationChange(event.target.value)}
                disabled={locationDisabled || locationLoading}
                error={Boolean(locationError)}
                helperText={locationError}
                InputProps={{
                  endAdornment: locationEndAdornment ? (
                    <InputAdornment position="end">
                      {locationEndAdornment}
                    </InputAdornment>
                  ) : null,
                }}>
                {!locationLoading && locationOptions.length > 0 && (
                  <MenuItem value="" disabled>
                    Select remote server
                  </MenuItem>
                )}
                {locationLoading && (
                  <MenuItem value="">Loading remote connections...</MenuItem>
                )}
                {!locationLoading && locationOptions.length === 0 && (
                  <MenuItem value="" disabled>
                    No remote connections found
                  </MenuItem>
                )}
                {!locationLoading &&
                  locationOptions.map((connection) => (
                    <MenuItem key={connection.id} value={connection.id}>
                      {connection.url}
                    </MenuItem>
                  ))}
              </TextField>
              {locationConnectAction}
            </Stack>
          </FormControl>
        )}
      </div>

      <div className="deploy-remote-tree-wrapper">
        <div className="deploy-remote-list-toolbar">
          <Typography variant="caption" className="deploy-remote-list-count">
            {itemCount}{" "}
            {itemCount === 1
              ? t("LBL_DEPLOY_ITEM_SINGULAR") || "ITEM"
              : t("LBL_DEPLOY_ITEM_PLURAL") || "ITEMS"}
          </Typography>
          <div className="deploy-remote-list-actions">
            <IconButton size="small" onClick={onExpandAll} title="Expand All">
              <KeyboardDoubleArrowDownIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={onCollapseAll}
              title="Collapse All">
              <KeyboardDoubleArrowUpIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {(headerType === "lhs" || headerType === "rhs") && (
          <div className="deploy-header deploy-remote-tree-header">
            <div className="deploy-header-tree-node">
              <span className="deploy-header-spacer-switcher"></span>
              <span className="deploy-header-spacer-icon"></span>
              <div className="deploy-header-indent-placeholder"></div>
              <div className="deploy-header-file-column">
                <div className="deploy-header-file-content">File</div>
              </div>
              <div className="deploy-header-data-columns">
                <div
                  className="deploy-header-server-rev-id"
                  style={{ textAlign: "center" }}>
                  Server Rev ID
                </div>
                <div
                  className="deploy-header-server-user"
                  style={{ textAlign: "center" }}>
                  Server Last Modified User
                </div>
                <div
                  className="deploy-header-server-modified"
                  style={{ textAlign: "center" }}>
                  Server Last Modified Date
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="deploy-scrollable-tree deploy-remote-tree-scroll">
          {loading ? (
            <div className="deploy-loading-container">
              <CircularProgress size={28} />
            </div>
          ) : (
            <Tree
              className="deploy-tree-custom deploy-tree"
              treeData={treeData}
              titleRender={titleRender}
              expandedKeys={expandedKeys}
              selectedKeys={selectedKeys}
              onSelect={onSelect}
              onExpand={onExpand}
              onRightClick={onRightClick}
              showLine
              autoExpandParent={false}
              switcherIcon={switcherIcon}
              showIcon={true}
              icon={renderNodeIcon}
              selectable={true}
              multiple={true}
            />
          )}
        </div>
      </div>
    </section>
  );

  const handleTreeSelectWithModifiers = useCallback(
    ({
      info,
      treeData,
      lastSelectedKeyRef,
      setSelectedKeys,
    }) => {
      const clickedKey = info?.node?.key;
      if (!clickedKey) {
        return;
      }

      const event = info?.nativeEvent || {};
      const isCtrlOrCmd = Boolean(event.ctrlKey || event.metaKey);
      const isShift = Boolean(event.shiftKey);

      if (isShift && lastSelectedKeyRef.current) {
        const orderedKeys = getAllTreeKeys(treeData || []);
        const startIndex = orderedKeys.indexOf(lastSelectedKeyRef.current);
        const endIndex = orderedKeys.indexOf(clickedKey);

        if (startIndex >= 0 && endIndex >= 0) {
          const [from, to] =
            startIndex <= endIndex
              ? [startIndex, endIndex]
              : [endIndex, startIndex];
          const rangeKeys = orderedKeys.slice(from, to + 1);
          setSelectedKeys((prev) =>
            Array.from(new Set([...(prev || []), ...rangeKeys])),
          );
        } else {
          setSelectedKeys([clickedKey]);
        }
      } else if (isCtrlOrCmd) {
        setSelectedKeys((prev) => {
          const previous = Array.isArray(prev) ? prev : [];
          return previous.includes(clickedKey)
            ? previous.filter((k) => k !== clickedKey)
            : [...previous, clickedKey];
        });
      } else {
        // Plain click: single select only.
        setSelectedKeys([clickedKey]);
      }

      lastSelectedKeyRef.current = clickedKey;
    },
    [getAllTreeKeys],
  );

  return (
    <div className="deploy-page-container">
      <div className="deploy-main-container">
        <div className="deploy-main-content deploy-remote-main-content">
          <div className="deploy-remote-header">
            <div className="deploy-remote-title-wrap">
              <span className="deploy-remote-title-icon">
                <SwapHorizIcon fontSize="small" />
              </span>
              <Typography variant="h5" className="deploy-remote-title">
                {t("LBL_DEPLOY_TRANSFER_MANAGER") || "Transfer Manager"}
              </Typography>
            </div>

            <div className="deploy-filter-checkbox-container deploy-remote-filter">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showNewerTemplates}
                    onChange={(event) =>
                      setShowNewerTemplates(event.target.checked)
                    }
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">
                    {t("LBL_DEPLOY_SHOW_NEWER_TEMPLATES") ||
                      "Show Newer Templates"}
                  </Typography>
                }
              />
            </div>
          </div>

          <Split
            className="split deploy-remote-workspace height-100"
            direction="horizontal"
            gutterSize={4}
            snapOffset={0}
            dragInterval={4}
            minSize={[260, 90, 260]}
            sizes={[46, 8, 46]}>
            {renderPanel({
              title: t("LBL_CURRENT_SERVER") || "Current Server",
              titleIcon: <LaptopMacOutlinedIcon fontSize="small" />,
              locationLabel: t("LBL_SERVER_URL") || "Server URL",
              locationValue: currentServer,
              onLocationChange: () => {},
              treeData: leftTreeDisplay,
              expandedKeys: leftExpandedKeys,
              selectedKeys: leftSelectedKeys,
              onExpand: (keys) => setLeftExpandedKeys(keys),
              onSelect: (_, info) =>
                handleTreeSelectWithModifiers({
                  info,
                  treeData: leftTreeDisplay,
                  lastSelectedKeyRef: leftLastSelectedKeyRef,
                  setSelectedKeys: setLeftSelectedKeys,
                }),
              titleRender: leftTitleRender,
              loading: loadingLeftTree,
              itemCount: countTreeItems(leftTreeDisplay),
              headerType: "lhs",
              onExpandAll: () =>
                setLeftExpandedKeys(getAllTreeKeys(leftTreeDisplay)),
              onCollapseAll: () => setLeftExpandedKeys([]),
              locationType: "text",
            })}

            <div className="deploy-remote-transfer-column deploy-remote-split-center">
              <Box
                variant="outlined"
                className="deploy-remote-transfer-icon-button"
                aria-label="Transfer templates">
                <SwapHorizIcon sx={{ fontSize: 24 }} />
              </Box>

              <Button
                variant="outlined"
                className="deploy-remote-transfer-button"
                endIcon={<ArrowForwardIcon fontSize="small" />}
                onClick={() => handleExport()}
                disabled={
                  leftSelectedKeys.length === 0 || !remoteToken || !serverLocation
                }>
                Export
              </Button>
            </div>

            {renderPanel({
              title: t("LBL_BRIDGEGATE_SERVER") || "BridgeGate Server",
              titleIcon: <CloudOutlinedIcon fontSize="small" />,
              locationLabel: t("LBL_SERVER_URL") || "Server URL",
              locationValue: serverLocation,
              onLocationChange: setServerLocation,
              treeData: rightTreeDisplay,
              expandedKeys: rightExpandedKeys,
              selectedKeys: rightSelectedKeys,
              onExpand: (keys) => setRightExpandedKeys(keys),
              onSelect: (_, info) =>
                handleTreeSelectWithModifiers({
                  info,
                  treeData: rightTreeDisplay,
                  lastSelectedKeyRef: rightLastSelectedKeyRef,
                  setSelectedKeys: setRightSelectedKeys,
                }),
              titleRender: rightTitleRender,
              loading: loadingRightTree,
              itemCount: countTreeItems(rightTreeDisplay),
              headerType: "rhs",
              onExpandAll: () =>
                setRightExpandedKeys(getAllTreeKeys(rightTreeDisplay)),
              onCollapseAll: () => setRightExpandedKeys([]),
              locationType: "select",
              locationOptions: remoteConnections,
              locationLoading: loadingRemoteConnections,
              locationError: remoteLoginError || remoteConnectionsHelperText,
              locationDisabled:
                !urlParams?.account || Boolean(remoteConnectionsHelperText),
              locationEndAdornment: (
                <Button
                  size="small"
                  onClick={handleOpenConnectionDrawer}
                  disabled={!urlParams?.account}
                  sx={{ minWidth: 40, px: 0.8 }}>
                  <MoreHorizIcon  />
                </Button>
              ),
              locationConnectAction: (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {remoteToken ? (
                    <Box
                      sx={{
                        border: "1px solid",
                        borderColor: "success.main",
                        borderRadius: 1,
                        px: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        height: 36,
                      }}>
                      <Typography
                        variant="caption"
                        color="success.main"
                        sx={{ fontWeight: 500 }}>
                        {t("LBL_REMOTE_CONN_CONNECTED") || "Connected"}
                      </Typography>
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleDisconnect}
                        sx={{ minWidth: 70, px: 0.5, height: 32 }}>
                        {t("LBL_REMOTE_CONN_DISCONNECT") || "Disconnect"}
                      </Button>
                    </Box>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={
                          !serverLocation ||
                          loadingRemoteConnections ||
                          Boolean(remoteConnectionsHelperText) ||
                          remoteLoginLoading
                        }
                        onClick={handleConnectWithSavedConnection}
                        startIcon={
                          remoteLoginLoading ? <CircularProgress size={14} /> : null
                        }
                        sx={{
                          minWidth: 90,
                          width: 90,
                          px: 1,
                          height: 36,
                        }}>
                        {remoteLoginLoading
                          ? t("LBL_REMOTE_CONN_CONNECTING") || "Connecting…"
                          : t("LBL_REMOTE_CONN_CONNECT") || "Connect"}
                      </Button>
                    </>
                  )}
                </Stack>
              ),
            })}
          </Split>
        </div>
      </div>

      <Drawer
        anchor="left"
        open={connectionDrawerOpen}
        onClose={handleCloseConnectionDrawer}
        sx={{
          "& .MuiDrawer-paper": {
            width: "100%",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            maxWidth: "100vw",
            overflow: "hidden",
            backgroundColor: "transparent",
            boxSizing: "border-box",
          },
        }}>
        <Split
          className="split height-100 flex"
          direction="horizontal"
          gutterSize={4}
          snapOffset={0}
          dragInterval={4}
          minSize={[320, 0]}
          sizes={[67, 33]}>
          <Paper
            square
            sx={{
              height: "100%",
              width: "100%",
              backgroundColor: "white",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "hidden",
            }}>
            <Box
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}>
              <Typography variant="h6">Create Remote Connection</Typography>
              <Typography variant="body2" color="text.secondary">
                Add a new remote BridgeGate server connection.
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  maxHeight: "calc(100vh - 220px)",
                  maxWidth: "100%",
                  overflowX: "hidden",
                  overflowY: "auto",
                }}>
              <CommonGrid
                contextMenuId="remote-connection-grid-target-id"
                showContextMenu={false}
                enableAutoBlur={false}
                rowData={connectionGridRows}
                gridOptions={connectionGridOptions}
                containerClasses={'account-data-grid-container'}
                containerStyle={{ height: "100%" }}
                getGridAPI={(gridApi) => setConnectionGridApi(gridApi)}
                gridProps={{
                  suppressHorizontalScroll: false,
                  alwaysShowHorizontalScroll: true,
                  suppressScrollOnNewData: true,
                  animateRows: false,
                  singleClickEdit: true,
                  stopEditingWhenCellsLoseFocus: true,
                  // Mark grid as dirty only when user actually edits a cell
                  onCellValueChanged: () => setHasConnectionChanges(true),
                }}
              />
              </Box>

              {newConnectionError && (
                <Typography variant="caption" color="error">
                  {newConnectionError}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                p: 2,
                backgroundColor: "white",
                bottom: 0,
              }}>
              <Button
                variant="outlined"
                onClick={handleSaveConnection}
                disabled={savingConnection || !hasConnectionChanges}>
                {savingConnection ? "Applying..." : "Apply"}
              </Button>
              <Button
                onClick={handleCloseConnectionDrawer}
                disabled={savingConnection}>
                Cancel
              </Button>
            </Box>
          </Paper>
          <Box></Box>
        </Split>
      </Drawer>
      <ConfirmModal
        openModal={deleteConnectionModalOpen}
        message={deleteConnectionMessage}
        handleClose={(isOpen) => {
          setDeleteConnectionModalOpen(isOpen);
          if (!isOpen) {
            setPendingDeleteConnectionRow(null);
          }
        }}
        yesHandler={handleConfirmDeleteConnection}
        noHandler={() => {
          setDeleteConnectionModalOpen(false);
          setPendingDeleteConnectionRow(null);
        }}
      />
      <ExportModal
        open={openExportCommentModal}
        handleClose={() => setOpenExportCommentModal(false)}
        selectedItems={buildSelectedItemsFromLeftTree()}
        remoteToken={remoteToken}
        urlParams={urlParams}
        remoteUrl={
          remoteConnections.find((c) => c.id === serverLocation)?.url || ""
        }
        onDeploySuccess={refreshRightTreeData}
      />
      <RemoteRevisionHistoryModal
        open={revisionHistoryModalOpen}
        onClose={() => {
          setRevisionHistoryModalOpen(false);
          setRevisionHistoryNode(null);
        }}
        node={revisionHistoryNode}
        remoteToken={remoteToken}
        remoteUrl={
          remoteConnections.find((c) => c.id === serverLocation)?.url || ""
        }
        urlParams={urlParams}
      />
    </div>
  );
}

export default DeployToRemoteServer;
